<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="1200"
    persistent
  >
    <v-card>
      <!-- Header -->
      <v-card-title class="pb-2 pt-3 px-4 orange white--text">
        <v-icon color="white" class="mr-2">mdi-history</v-icon>
        <span class="text-h6 font-weight-bold">Seller Product Variant Upload History</span>
        <v-spacer></v-spacer>
        <v-btn 
          icon 
          @click="closeDialog"
          color="white"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Subtitle -->
      <v-card-subtitle class="py-2 px-4 orange white--text">
        <p class="mb-0 text-caption">
          View and manage your seller product variant bulk upload history
        </p>
      </v-card-subtitle>

      <v-divider></v-divider>

      <!-- Filters -->
      <v-card-text class="pb-2 px-4">
        <v-row>
          <!-- Search Field -->
          <v-col cols="12" md="4">
            <v-text-field
              v-model="searchKeyword"
              label="Search Uploads"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              placeholder="Filename, notes..."
              hide-details
              @click:clear="searchKeyword = null; fetchUploads()"
              @keydown.enter="fetchUploads()"
            ></v-text-field>
          </v-col>

          <!-- Status Filter -->
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="filters.status"
              :items="statusItems"
              label="Status"
              prepend-inner-icon="mdi-toggle-switch"
              outlined
              dense
              clearable
              hide-details
              @change="fetchUploads()"
            ></v-autocomplete>
          </v-col>

          <!-- User Filter -->
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="filters.user_id"
              :items="userItems"
              label="Uploaded By"
              prepend-inner-icon="mdi-account"
              outlined
              dense
              clearable
              hide-details
              @change="fetchUploads()"
            ></v-autocomplete>
          </v-col>

          <!-- Show Deleted -->
          <v-col cols="12" md="2">
            <v-switch
              v-model="showDeleted"
              label="Show Deleted"
              @change="fetchUploads()"
              dense
              hide-details
              color="orange"
            ></v-switch>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider class="mx-4"></v-divider>

      <!-- Content -->
      <v-card-text class="pa-4" style="max-height: 70vh; overflow-y: auto;">
        <!-- Statistics Cards -->
        <v-row class="mb-4" v-if="statistics">
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center pa-3">
                <v-icon size="24" color="primary">mdi-upload</v-icon>
                <div class="text-h6 font-weight-bold">{{ statistics.total_uploads }}</div>
                <div class="text-caption">Total Uploads</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center pa-3">
                <v-icon size="24" color="success">mdi-check-circle</v-icon>
                <div class="text-h6 font-weight-bold text-success">{{ statistics.successful_uploads }}</div>
                <div class="text-caption">Successful</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center pa-3">
                <v-icon size="24" color="error">mdi-alert-circle</v-icon>
                <div class="text-h6 font-weight-bold text-error">{{ statistics.failed_uploads }}</div>
                <div class="text-caption">Failed</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center pa-3">
                <v-icon size="24" color="info">mdi-package-variant</v-icon>
                <div class="text-h6 font-weight-bold text-info">{{ statistics.total_records_processed }}</div>
                <div class="text-caption">Records Processed</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-6">
          <v-progress-circular
            indeterminate
            color="orange"
            size="60"
          ></v-progress-circular>
          <p class="mt-3 grey--text">Loading upload history...</p>
        </div>

        <!-- Data Table -->
        <v-data-table
          v-else
          :headers="headers"
          :items="uploads"
          :server-items-length="totalItems"
          :page.sync="currentPage"
          :items-per-page="itemsPerPage"
          hide-default-footer
          class="elevation-1"
        >
          <!-- File Info Column -->
          <template #[`item.file_info`]="{ item }">
            <div>
              <div class="d-flex align-center">
                <v-icon 
                  :color="item.file_path ? 'success' : 'error'" 
                  x-small 
                  class="mr-1"
                >
                  {{ item.file_path ? 'mdi-file-check' : 'mdi-file-remove' }}
                </v-icon>
                <div class="font-weight-medium text-body-2">
                  {{ item.original_filename }}
                </div>
              </div>
              <div class="text-caption grey--text">
                Uploaded: {{ formatDate(item.created_at) }}
              </div>
              <div v-if="item.file_path" class="text-caption success--text">
                File available in cloud storage
              </div>
              <div v-else class="text-caption error--text">
                File not found
              </div>
            </div>
          </template>

          <!-- User Column -->
          <template #[`item.user`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="24" class="mr-2" color="orange">
                <span class="white--text text-caption">
                  {{ getUserInitials(item.user) }}
                </span>
              </v-avatar>
              <div>
                <div class="font-weight-medium text-body-2">
                  {{ getUserName(item.user) }}
                </div>
              </div>
            </div>
          </template>

          <!-- Progress Column -->
          <template #[`item.progress`]="{ item }">
            <div>
              <v-progress-linear
                :value="getProgressPercentage(item)"
                :color="getProgressColor(item)"
                height="6"
                rounded
              ></v-progress-linear>
              <div class="text-caption mt-1">
                {{ item.successful_rows }}/{{ item.total_rows }}
              </div>
            </div>
          </template>

          <!-- Status Column -->
          <template #[`item.status`]="{ item }">
            <v-chip
              small
              :color="getStatusColor(item.status)"
              :text-color="getStatusTextColor(item.status)"
            >
              <v-icon left x-small>{{ getStatusIcon(item.status) }}</v-icon>
              {{ getStatusText(item.status) }}
            </v-chip>
          </template>

          <!-- Success Rate Column -->
          <template #[`item.success_rate`]="{ item }">
            <div class="text-center">
              <v-chip
                x-small
                :color="getSuccessRateColor(item)"
                text-color="white"
              >
                {{ getSuccessRate(item) }}%
              </v-chip>
            </div>
          </template>

          <!-- Notes Column -->
          <template #[`item.notes`]="{ item }">
            <div class="text-caption" style="max-width: 150px;">
              {{ item.notes || '-' }}
            </div>
          </template>

          <!-- Actions Column -->
          <template #[`item.actions`]="{ item }">
            <div class="d-flex">
              <v-btn
                icon
                x-small
                color="primary"
                @click="viewDetails(item)"
                class="mr-1"
              >
                <v-icon x-small>mdi-eye</v-icon>
              </v-btn>
              
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    color="info"
                    @click="downloadOriginalFile(item)"
                    class="mr-1"
                    :disabled="!item.file_path"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon x-small>mdi-file-download-outline</v-icon>
                  </v-btn>
                </template>
                <span>Download Original File</span>
              </v-tooltip>
              
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-if="item.status === 'completed' && item.failed_rows > 0"
                    icon
                    x-small
                    color="error"
                    @click="downloadErrors(item)"
                    class="mr-1"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon x-small>mdi-alert-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Download Error Report</span>
              </v-tooltip>

              <v-btn
                v-if="!item.is_deleted"
                icon
                x-small
                color="error"
                @click="confirmDelete(item)"
              >
                <v-icon x-small>mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>

        <!-- Pagination -->
        <div class="d-flex justify-center mt-4">
          <v-pagination
            v-model="currentPage"
            :length="Math.ceil(totalItems / itemsPerPage)"
            :total-visible="7"
            color="orange"
            @input="fetchUploads"
            circle
          ></v-pagination>
        </div>
      </v-card-text>

      <!-- Footer Actions -->
      <v-divider></v-divider>
      <v-card-actions class="px-4 py-3">
        <v-btn
          color="orange"
          outlined
          @click="refreshData"
          :loading="isLoading"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog">Close</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Upload Details Dialog -->
    <v-dialog v-model="showDetailsDialog" max-width="800" scrollable>
      <v-card v-if="selectedUpload">
        <v-card-title class="primary white--text">
          <v-icon color="white" class="mr-2">mdi-file-document-outline</v-icon>
          Upload Details
          <v-spacer></v-spacer>
          <v-btn icon @click="showDetailsDialog = false" color="white">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Upload Summary -->
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <v-card outlined>
                <v-card-title class="py-2">Basic Information</v-card-title>
                <v-card-text>
                  <div class="mb-2">
                    <strong>Filename:</strong> {{ selectedUpload.original_filename }}
                  </div>
                  <div class="mb-2">
                    <strong>Uploaded By:</strong> {{ getUserName(selectedUpload.user) }}
                  </div>
                  <div class="mb-2">
                    <strong>Upload Date:</strong> {{ formatDate(selectedUpload.created_at) }}
                  </div>
                  <div class="mb-2">
                    <strong>Status:</strong> 
                    <v-chip small :color="getStatusColor(selectedUpload.status)">
                      {{ getStatusText(selectedUpload.status) }}
                    </v-chip>
                  </div>
                  <div v-if="selectedUpload.notes">
                    <strong>Notes:</strong> {{ selectedUpload.notes }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card outlined>
                <v-card-title class="py-2">Statistics</v-card-title>
                <v-card-text>
                  <div class="mb-2">
                    <strong>Total Rows:</strong> {{ selectedUpload.total_rows }}
                  </div>
                  <div class="mb-2">
                    <strong>Successful:</strong> 
                    <span class="text-success">{{ selectedUpload.successful_rows }}</span>
                  </div>
                  <div class="mb-2">
                    <strong>Failed:</strong> 
                    <span class="text-error">{{ selectedUpload.failed_rows }}</span>
                  </div>
                  <div>
                    <strong>Success Rate:</strong> 
                    <span :class="getSuccessRateColor(selectedUpload)">
                      {{ getSuccessRate(selectedUpload) }}%
                    </span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Errors Section -->
          <v-card v-if="selectedUpload.errors && selectedUpload.errors.length > 0" outlined color="error">
            <v-card-title class="error white--text py-2">
              <v-icon color="white" class="mr-2">mdi-alert</v-icon>
              Upload Errors
            </v-card-title>
            <v-card-text class="pa-0">
              <v-expansion-panels>
                <v-expansion-panel v-for="(error, index) in selectedUpload.errors.slice(0, 10)" :key="index">
                  <v-expansion-panel-header>
                    <strong>Row {{ error.row_number || (index + 1) }}:</strong>
                    <span class="ml-2">{{ error.data?.product_name || error.message || 'Error' }}</span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <pre class="text-caption">{{ JSON.stringify(error, null, 2) }}</pre>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
              <div v-if="selectedUpload.errors.length > 10" class="pa-3 text-center">
                <span class="text-caption">
                  Showing first 10 errors. Total: {{ selectedUpload.errors.length }} errors.
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDetailsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog.show" max-width="400" persistent>
      <v-card>
        <v-card-title class="error white--text">
          <v-icon color="white" class="mr-2">mdi-delete-alert</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="pa-4">
          <p>Are you sure you want to delete this upload record?</p>
          <div class="grey lighten-4 pa-2 rounded">
            <div class="text-caption">
              <strong>File:</strong> {{ deleteDialog.item?.original_filename }}<br />
              <strong>Uploaded:</strong> {{ formatDate(deleteDialog.item?.created_at) }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog.show = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteUpload" :loading="deleteDialog.loading">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import axios from "@/axios";

export default {
  name: "SellerProductVariantUploadHistory",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      uploads: [],
      statistics: null,
      isLoading: false,
      searchKeyword: "",
      showDeleted: false,
      totalItems: 0,
      currentPage: 1,
      itemsPerPage: 10,
      
      filters: {
        status: null,
        user_id: null,
      },
      
      // Dialog states
      showDetailsDialog: false,
      selectedUpload: null,
      
      deleteDialog: {
        show: false,
        item: null,
        loading: false,
      },
      
      // Data for filters
      statusItems: [
        { text: "Pending", value: "pending" },
        { text: "Processing", value: "processing" },
        { text: "Completed", value: "completed" },
        { text: "Failed", value: "failed" },
      ],
      userItems: [], // Will be populated from API
      
      headers: [
        { text: "File Info", value: "file_info", sortable: false, width: "200px" },
        { text: "Uploaded By", value: "user", sortable: false, width: "150px" },
        { text: "Progress", value: "progress", sortable: false, width: "120px" },
        { text: "Status", value: "status", sortable: false, width: "120px" },
        { text: "Success Rate", value: "success_rate", sortable: false, width: "100px" },
        { text: "Notes", value: "notes", sortable: false, width: "150px" },
        { text: "Actions", value: "actions", sortable: false, width: "120px", align: "center" },
      ],
    };
  },
  
  watch: {
    value(newVal) {
      if (newVal) {
        this.fetchUploads();
        this.fetchStatistics();
      }
    },
  },
  
  methods: {
    async fetchUploads() {
      try {
        this.isLoading = true;
        
        const params = {
          page: this.currentPage,
          rowsPerPage: this.itemsPerPage,
          show_deleted: this.showDeleted,
        };
        
        if (this.searchKeyword) {
          params.search_keyword = this.searchKeyword;
        }
        
        Object.keys(this.filters).forEach((key) => {
          if (this.filters[key] !== null) {
            params[key] = this.filters[key];
          }
        });
        
        const response = await axios.get("/seller-product-variant-uploads", {
          params,
        });
        
        this.uploads = response.data.data || [];
        this.totalItems = response.data.count || 0;
      } catch (error) {
        console.error("Error fetching uploads:", error);
        this.$toast.error("Failed to load upload history");
      } finally {
        this.isLoading = false;
      }
    },
    
    async fetchStatistics() {
      try {
        const response = await axios.get("/seller-product-variant-uploads/statistics");
        this.statistics = response.data.data;
      } catch (error) {
        console.error("Error fetching statistics:", error);
      }
    },
    
    async viewDetails(upload) {
      try {
        const response = await axios.get(`/seller-product-variant-uploads/${upload.id}`);
        this.selectedUpload = response.data.data;
        this.showDetailsDialog = true;
      } catch (error) {
        console.error("Error fetching upload details:", error);
        this.$toast.error("Failed to load upload details");
      }
    },
    
    async downloadOriginalFile(upload) {
      try {
        const response = await axios.get(`/seller-product-variant-uploads/${upload.id}/download`, {
          responseType: "blob",
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.download = upload.original_filename || `upload_${upload.id}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        this.$toast.success("Original file downloaded successfully");
      } catch (error) {
        console.error("Error downloading original file:", error);
        this.$toast.error("Failed to download original file");
      }
    },
    
    async downloadErrors(upload) {
      try {
        const response = await axios.get(`/seller-product-variant-uploads/${upload.id}/errors`, {
          responseType: "blob",
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.download = `seller_product_variant_errors_${upload.id}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        this.$toast.success("Error report downloaded successfully");
      } catch (error) {
        console.error("Error downloading errors:", error);
        this.$toast.error("Failed to download error report");
      }
    },
    
    confirmDelete(upload) {
      this.deleteDialog.item = upload;
      this.deleteDialog.show = true;
    },
    
    async deleteUpload() {
      try {
        this.deleteDialog.loading = true;
        
        await axios.delete(`/seller-product-variant-uploads/${this.deleteDialog.item.id}`);
        
        this.$toast.success("Upload record deleted successfully");
        this.deleteDialog.show = false;
        this.fetchUploads();
        this.$emit('refresh-needed');
      } catch (error) {
        console.error("Error deleting upload:", error);
        this.$toast.error("Failed to delete upload record");
      } finally {
        this.deleteDialog.loading = false;
      }
    },
    
    refreshData() {
      this.fetchUploads();
      this.fetchStatistics();
    },
    
    closeDialog() {
      this.$emit("input", false);
    },
    
    // Helper methods
    formatDate(dateString) {
      return new Date(dateString).toLocaleString();
    },
    
    getUserName(user) {
      if (!user) return "N/A";
      return (
        `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
        user.email ||
        "N/A"
      );
    },
    
    getUserInitials(user) {
      if (!user) return "NA";
      const firstName = user.first_name || "";
      const lastName = user.last_name || "";
      return (
        `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "NA"
      );
    },
    
    getProgressPercentage(upload) {
      if (upload.total_rows === 0) return 0;
      return (upload.successful_rows / upload.total_rows) * 100;
    },
    
    getProgressColor(upload) {
      const percentage = this.getProgressPercentage(upload);
      if (percentage >= 80) return "success";
      if (percentage >= 60) return "warning";
      return "error";
    },
    
    getStatusColor(status) {
      switch (status) {
        case "pending": return "grey";
        case "processing": return "info";
        case "completed": return "success";
        case "failed": return "error";
        default: return "grey";
      }
    },
    
    getStatusTextColor(status) {
      return status === "pending" ? "black" : "white";
    },
    
    getStatusIcon(status) {
      switch (status) {
        case "pending": return "mdi-clock";
        case "processing": return "mdi-loading mdi-spin";
        case "completed": return "mdi-check";
        case "failed": return "mdi-close";
        default: return "mdi-help";
      }
    },
    
    getStatusText(status) {
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    
    getSuccessRate(upload) {
      if (upload.total_rows === 0) return 0;
      return Math.round((upload.successful_rows / upload.total_rows) * 100);
    },
    
    getSuccessRateColor(upload) {
      const rate = this.getSuccessRate(upload);
      if (rate >= 80) return "success";
      if (rate >= 60) return "warning";
      return "error";
    },
  },
};
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.text-error {
  color: #F44336 !important;
}

.text-success {
  color: #4CAF50 !important;
}

.text-warning {
  color: #FF9800 !important;
}

.text-info {
  color: #2196F3 !important;
}

pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>